import { buildGroupTicketV2ElementsConfigUrl } from './groupTicketContract';
import { getBackendUrl, isDevelopment } from '../config/env';

/**
 * The two decisions EmbedForm has to make BEFORE its first paint, extracted so
 * the first frame and the fetch cannot disagree with each other (CON-21).
 *
 * Both were previously inline: the url inside `fetchElementsConfig`, the page
 * choice inside the effect that runs after the response lands. Neither could be
 * consulted while choosing what to render first, which is why a mount holding a
 * payload a sibling had already resolved still had to start on the placeholder.
 */

/**
 * The elements-config url this mount will read, or null when it will not read
 * one at all.
 *
 * Null cases mirror `fetchElementsConfig`'s own early returns exactly: a preview
 * of a form with no backing element, an overlay handed its payload by
 * `OverlayElement`, and a missing backend url.
 *
 * @param {object} args
 * @param {string|null} args.formID
 * @param {boolean} args.previewMode
 * @param {object|null} args.previewData
 * @param {'dynamic'|'overlay'} args.formDisplayMode
 * @param {string|null} args.backendUrl - the `backendUrl` prop, if the host passed one
 * @returns {string|null}
 */
export function resolveElementsConfigUrl({
  formID,
  previewMode = false,
  previewData = null,
  formDisplayMode = 'dynamic',
  backendUrl: propBackendUrl = null,
}) {
  let elementID = formID;

  if (previewMode && (elementID === 'NEW_FORM' || !elementID || elementID === 'preview')) {
    return null;
  }
  if (formDisplayMode === 'overlay' && previewData) {
    return null;
  }
  if (elementID === 'NEW_FORM' || !elementID) {
    elementID = isDevelopment()
      ? '391e9af2-d97e-4e22-bc6f-b3ad4c2cf63b'
      : '318bfedb-b2ca-497a-8b7f-9d0cccf6fb0c';
  }

  const backendUrl = propBackendUrl || getBackendUrl();
  if (!backendUrl) return null;

  return buildGroupTicketV2ElementsConfigUrl({ backendUrl, elementID });
}

/**
 * Which of the widget's two pages a given configuration opens on.
 *
 * @param {object} args
 * @param {object|null} args.data
 * @param {boolean} args.isHostedPage
 * @param {boolean} args.hostedPagePreview
 * @param {'dynamic'|'overlay'} args.formDisplayMode
 * @param {boolean} args.isDrawerOpen
 * @returns {'FORM'|'DETAIL_PAGE'}
 */
export function resolveFormPage({
  data,
  isHostedPage = false,
  hostedPagePreview = false,
  formDisplayMode = 'dynamic',
  isDrawerOpen = false,
}) {
  // A hosted page, an overlay and a drawer all open straight on the form: each
  // of them already shows the campaign content the detail page would repeat.
  if (isHostedPage || hostedPagePreview || formDisplayMode === 'overlay' || isDrawerOpen) {
    return 'FORM';
  }
  return data?.enableEmbedForm === true ? 'DETAIL_PAGE' : 'FORM';
}
